import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { V2Alert } from '@abyss/web/ui/Alert';
import { Box } from '@abyss/web/ui/Box';
import { DropdownMenu } from '@abyss/web/ui/DropdownMenu';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { find } from 'lodash';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { getChipValue } from '../../frontends/GuestExperience/Guest.utils';
import { GuestPlanRequiredModal } from '../../frontends/GuestExperience/modals/GuestPlanRequiredModal';
import { getActualLanguageCode } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  LANGUAGES,
  LanguageObject,
  Locale,
} from '../../frontends/ProviderSearch/context/Internationalization/types';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { FontSizeModal } from './FontSizeModal';
import { GuestLogo } from './GuestLogo';
import { SignIn } from './SignIn';
import { getHeaderContent } from './utils';

interface MenuItem {
  label: string;
  value: Locale;
  checked: boolean;
  onChange: () => void;
}

export const GuestHeader = () => {
  const { t, i18n } = useTranslation();
  const { navigate, getSearchParams } = useRouter();
  const { pathname } = useLocation();
  const [searchParams] = getSearchParams();
  const lagoonUIMessage = useLagoon('ui-messaging')();
  const configuration = useLagoon('header-configuration')();
  const coverageTypeFromStorage = useCoverageType();
  const [enableGuestHeader] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GUEST_HEADER,
  ]);
  const obj = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.SELECTED_PLAN_DETAILS
  );

  const [hasUserClosedSignInBanner, setHasUserClosedSignInBanner] = useState(
    sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.IS_GUEST_SIGN_IN_BANNER_DISMISSED
    ) === 'true'
  );

  const { planYear, isDual = false } = obj ? JSON.parse(obj) : {};

  const coverageTypeFromParams = searchParams?.get('coverageType');
  const url = window.location.href;
  const isFindcareLoaded = url.includes('/browse');

  const coverage =
    coverageTypeFromParams ||
    (isFindcareLoaded ? coverageTypeFromStorage : 'ALL') ||
    'ALL';
  const planSelectionData = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA
  );

  const { selectedPlan, lob } = planSelectionData
    ? JSON.parse(planSelectionData)
    : {};
  const lobFromParams = searchParams?.get('planSelectionLob') || 'EI';

  const headerData = useMemo(
    () =>
      getHeaderContent(
        configuration,
        isFindcareLoaded ? lob : lobFromParams,
        coverage
      ),
    [configuration, isFindcareLoaded, lob, lobFromParams, coverage]
  );
  const chipValue = getChipValue(coverage);
  const signInConfig = useMemo(() => {
    const config = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_HEADER_SIGN_IN,
    });
    const locale = i18n.language.split('-')?.[0] || 'en';
    return {
      text: config?.message,
      url: headerData?.signinUrl
        ? `${headerData?.signinUrl}?locale=${locale}`
        : config?.url,
    };
  }, [lagoonUIMessage, headerData?.signinUrl, i18n.language]);

  const uhcContinueConfig = useMemo(() => {
    const config = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION
        .GUEST_HEADER_PREPLAN_UHC_CONTINUE,
    });
    return {
      text: config?.message,
      url: config?.url || 'https://www.uhc.com/find-a-doctor',
    };
  }, [lagoonUIMessage]);

  const signInBannerConfig = useMemo(() => {
    const bannerText = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_SIGN_IN_BANNER,
    })?.message;

    const bannerButtonData = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.GUEST_SIGN_IN_BANNER_BUTTON,
    });

    const locale = i18n.language.split('-')?.[0] || 'en';
    return {
      text: bannerText,
      buttonText: bannerButtonData?.message,
      url: headerData?.signinUrl
        ? `${headerData?.signinUrl}?locale=${locale}`
        : bannerButtonData?.url,
    };
  }, [lagoonUIMessage, headerData?.signinUrl, i18n.language]);

  const supportedLanguages = headerData?.languages
    .split(',')
    .map((lang) => lang.trim()) || [Locale.enUS, Locale.esUS];

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale, setLocale } = chipStore;

  const updateLanguage = useCallback(async (chosenLanguage: LanguageObject) => {
    storage.cookie.set(Constants.PSLOCALE, chosenLanguage.locale);
    setLocale(chosenLanguage.locale);
    i18n.changeLanguage(chosenLanguage.locale);
  }, []);

  useLayoutEffect(() => {
    const chosenLanguage =
      LANGUAGES[getActualLanguageCode(locale as Locale, supportedLanguages)];
    updateLanguage(chosenLanguage);
  }, [pathname]);

  const [isPlanRequiredModalOpen, setIsPlanRequiredModalOpen] = useState(false);

  const handleOnLanguageChange = async (locale: Locale) => {
    const chosenLanguage =
      LANGUAGES[getActualLanguageCode(locale, supportedLanguages)];
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
      JSON.stringify(supportedLanguages)
    );
    await updateLanguage(chosenLanguage);
  };

  const logoClick = () => {
    // If on FindCare browse, keep existing redirect logic
    if (isFindcareLoaded) {
      window.location.assign(
        `/guest-plan-selection/browse?chipValue=${chipValue}&coverageType=${coverage}`
      );
      return;
    }
    // Otherwise, open the modal
    setIsPlanRequiredModalOpen(true);
  };

  const handlePlanRequiredClose = () => {
    setIsPlanRequiredModalOpen(false);
  };

  const languageMenuItems = [
    {
      checkboxes: Object?.values(LANGUAGES)?.reduce<MenuItem[]>((acc, item) => {
        if (supportedLanguages?.includes(item?.locale)) {
          acc.push({
            label: item.name,
            value: item.locale,
            checked: locale === item.locale,
            onChange: () => {
              handleOnLanguageChange(item?.locale);
            },
          });
        }
        return acc;
      }, []),
    },
  ];

  if (!enableGuestHeader) {
    return null;
  }

  const getHyperLink = (
    e: ReactMouseEvent<HTMLAnchorElement>,
    locale: string
  ) => {
    e.preventDefault();
    switch (locale) {
      case Locale.enUS:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
      case Locale.esUS:
        return window.open('https://es.uhc.com/contact-us', '_blank');
      default:
        return window.open(
          'https://www.uhc.com/contact-us?locale=en-US',
          '_blank'
        );
    }
  };

  return (
    <Box
      css={{
        'abyss-box-root': {
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '$white',
          width: '100%',
          height: 'auto',
          opacity: 1,
          fontFamily: '$primary',
          marginTop: '0px',
          padding: '0px',
        },
      }}
    >
      <Flex
        css={{
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '16px',
          height: '40px',
          backgroundColor: '#F3F3F3',
          padding: '0 48px',
        }}
      >
        <DropdownMenu
          after={<IconSymbol icon="keyboard_arrow_down" />}
          css={{
            fontSize: '$sm',
            'abyss-dropdown-menu-trigger': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&[data-state="open"]': {
                backgroundColor: 'white',
              },
            },
          }}
          data-testid="language-dropdown"
          label={
            Object.values(LANGUAGES).find((lang) => lang.locale === locale)
              ?.title || LANGUAGES?.en.name
          }
          menuItems={languageMenuItems}
          outline={false}
          width={110}
        />
        <FontSizeModal />
        <Link
          css={{
            fontSize: '$sm',
            textDecoration: 'none',
            '& .abyss-link-icon': { display: 'none' },
          }}
          data-testid="help-link"
          href={'#'}
          onClick={(e) => getHyperLink(e, locale)}
          openNewWindow={true}
        >
          {t('HELP')}
        </Link>
      </Flex>
      <Flex
        css={{
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '56px',
          margin: '11px 0',
          padding: '0 48px',
        }}
      >
        <GuestLogo headerData={headerData} logoClick={logoClick} />
        <SignIn
          locale={locale}
          text={signInConfig.text}
          url={signInConfig.url}
        />
      </Flex>
      <Box
        css={{
          alignItems: 'center',
          height: '36px',
          lineHeight: '36px',
          backgroundColor: '#002677',
          color: '#ffffff',
          padding: '0 48px',
        }}
      >
        {isFindcareLoaded && (selectedPlan?.planName || planYear) && (
          <Box
            css={{
              alignItems: 'center',
              flexDirection: 'row',
              display: 'flex',
              backgroundColor: '#002677',
              color: '#ffffff',
              gap: '8px',
            }}
          >
            <Flex
              css={{
                alignItems: 'center',
                gap: '4px',
                fontSize: '$sm',
                fontWeight: 500,
              }}
            >
              <span style={{ fontWeight: 700 }}>{t('PLAN')}</span>
              <span
                data-testid="guest-plan-name-link"
                onClick={() => {
                  navigate(
                    `/plan-selection?planSelectionLob=${lob}&coverageType=${
                      coverage || 'M'
                    }&chipValue=${chipValue}`
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(
                      `/plan-selection?planSelectionLob=${lob}&coverageType=${
                        coverage || 'M'
                      }&chipValue=${chipValue}`
                    );
                  }
                }}
                role="link"
                style={{
                  cursor: 'pointer',
                  color: '#ffffff',
                }}
                tabIndex={0}
              >
                {selectedPlan?.planName}
              </span>
            </Flex>
            {isDual && (
              <>
                <IconSymbol
                  color="#ffffff"
                  fill="#ffffff"
                  icon="arrow_forward_ios"
                  size="16px"
                />
                <Flex
                  css={{
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '$sm',
                    fontWeight: 500,
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{t('YEAR')}</span>
                  <span
                    data-testid="guest-plan-year-link"
                    onClick={() => {
                      navigate(
                        `/plan-year-selection?planSelectionLob=${lob}&coverageType=${
                          coverage || 'M'
                        }&chipValue=${chipValue}`
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(
                          `/plan-year-selection?planSelectionLob=${lob}&coverageType=${
                            coverage || 'M'
                          }&chipValue=${chipValue}`
                        );
                      }
                    }}
                    role="link"
                    style={{
                      cursor: 'pointer',
                      color: '#ffffff',
                    }}
                    tabIndex={0}
                  >
                    {planYear}
                  </span>
                </Flex>
              </>
            )}
          </Box>
        )}
      </Box>

      {isFindcareLoaded && !hasUserClosedSignInBanner && (
        <V2Alert
          cta={{
            type: 'button',
            props: {
              children: signInBannerConfig.buttonText,
              href: signInBannerConfig.url,
            },
          }}
          data-testid="guest-sign-in-banner"
          onClose={() => {
            setHasUserClosedSignInBanner(true);
            sessionStorage.setItem(
              Constants.STORAGE_KEYS.SESSION.IS_GUEST_SIGN_IN_BANNER_DISMISSED,
              'true'
            );
          }}
          status="info"
        >
          {signInBannerConfig.text}
        </V2Alert>
      )}
      {isPlanRequiredModalOpen && (
        <GuestPlanRequiredModal
          isOpen={isPlanRequiredModalOpen}
          onClose={handlePlanRequiredClose}
          signInUrl={signInConfig.url || '#'}
          uhcContinueUrl={uhcContinueConfig.url}
        />
      )}
    </Box>
  );
};

export default GuestHeader;
